import React, { useState } from 'react';
import {
  FluentProvider,
  webLightTheme,
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
} from '@fluentui/react-components';
import { CheckmarkCircle20Filled } from '@fluentui/react-icons';
import { useSettings } from '../../services/SettingsContext';
import { useAuth } from '../../services/AuthContext';
import { getAccessToken } from '../../services/oidc';
import { extractModelsFromToken, type Model } from '../../services/CapsifiService';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    zIndex: '50',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: tokens.spacingHorizontalXXL,
    boxShadow: tokens.shadow28,
  },
  title: {
    display: 'block',
    marginBottom: tokens.spacingVerticalS,
  },
  subtitle: {
    display: 'block',
    marginBottom: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground3,
  },
  modelList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  modelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    textAlign: 'left',
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  modelButtonSelected: {
    borderColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  cancelRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: tokens.spacingVerticalXL,
  },
  checkIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: '0',
  },
});

function ModelSelectorContent() {
  const classes = useStyles();
  const { apiSettings, selectModel, setShowModelSelector } = useSettings();
  const { logout } = useAuth();

  const [models, setModels] = useState<Model[]>([]);
  React.useEffect(() => {
    let active = true;
    void getAccessToken().then((token) => {
      if (active) setModels(token ? extractModelsFromToken(token) : []);
    });
    return () => {
      active = false;
    };
  }, []);

  // Auto-select when there is exactly one model
  React.useEffect(() => {
    if (models.length === 1) {
      selectModel(models[0].id, models[0].label);
    }
  }, [models, selectModel]);

  // While auto-selecting, render nothing to avoid flash
  if (models.length === 1) return null;

  const hasExistingSelection = Boolean(apiSettings.selectedModelId);

  return (
    <div className={classes.overlay}>
      <div className={classes.card}>
        <Text as="h1" size={700} weight="semibold" className={classes.title}>
          Select a model
        </Text>
        <Text size={300} className={classes.subtitle}>
          Choose the business model you want to work with in this session.
        </Text>

        {models.length === 0 ? (
          <div className={classes.emptyState}>
            <Text>No models found in your session.</Text>
            <Button appearance="outline" onClick={logout}>Log out</Button>
          </div>
        ) : (
          <div className={classes.modelList}>
            {models.map(model => {
              const isSelected = apiSettings.selectedModelId === model.id;
              return (
                <button
                  key={model.id}
                  className={mergeClasses(classes.modelButton, isSelected && classes.modelButtonSelected)}
                  onClick={() => selectModel(model.id, model.label)}
                >
                  <Text weight="semibold">{model.label}</Text>
                  {isSelected && <CheckmarkCircle20Filled className={classes.checkIcon} />}
                </button>
              );
            })}
          </div>
        )}

        {hasExistingSelection && (
          <div className={classes.cancelRow}>
            <Button appearance="subtle" onClick={() => setShowModelSelector(false)}>Cancel</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ModelSelectorPage() {
  return (
    <FluentProvider theme={webLightTheme}>
      <ModelSelectorContent />
    </FluentProvider>
  );
}
